import React, { useState, useEffect } from 'react';
import Taskbar from './Taskbar';
import DesktopIcon from './DesktopIcon';
import Window from './Window';
import AdminPanel from './AdminPanel';
import { useWindows } from '../contexts/WindowContext';

// Import local icons
import folderIcon from '../assets/icons/fodler.png';
import documentIcon from '../assets/icons/blank-document.png';
import desktopIcon from '../assets/icons/desktop.png';
import liveIcon from '../assets/icons/live.png';
import eventsIcon from '../assets/icons/events.svg';
import chatIcon from '../assets/icons/chat.svg';

type IconType = 'folder' | 'file' | 'game';

interface LiveStreamData {
  title: string;
  stream: MediaStream;
  source: 'camera' | 'screen';
}

interface DesktopIconItem {
  id: string;
  name: string;
  icon: string;
  x: number;
  y: number;
  type: IconType;
}

interface SelectionRectangle {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  isSelecting: boolean;
}

const Desktop: React.FC = () => {
  const { windows } = useWindows();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isStreamLive, setIsStreamLive] = useState(false);
  const [liveStreamData, setLiveStreamData] = useState<LiveStreamData | null>(null);
  
  const defaultIcons: DesktopIconItem[] = [
    // Column 1
    { id: 'my-computer', name: 'My Computer', icon: desktopIcon, x: 20, y: 20, type: 'folder' },
    { id: 'text-file', name: 'New Text Document.txt', icon: documentIcon, x: 20, y: 120, type: 'file' },
    { id: 'games', name: 'Games', icon: folderIcon, x: 20, y: 220, type: 'folder' },
    
    // Column 2
    { id: 'live', name: 'Live Stream', icon: liveIcon, x: 120, y: 20, type: 'file' },
    { id: 'events', name: 'Events', icon: eventsIcon, x: 120, y: 120, type: 'file' },
    { id: 'chat', name: 'Live Chat', icon: chatIcon, x: 120, y: 220, type: 'file' },
    
    // Column 3
    { id: 'visual', name: 'Visual', icon: '🎵', x: 220, y: 20, type: 'file' },
  ];

  // Selection rectangle state
  const [selectionRect, setSelectionRect] = useState<SelectionRectangle>({
    startX: 0,
    startY: 0,
    endX: 0,
    endY: 0,
    isSelecting: false,
  });

  // Load icon positions from localStorage or use defaults
  const loadIconPositions = (): DesktopIconItem[] => {
    try {
      const saved = localStorage.getItem('desktop-icon-positions');
      if (saved) {
        const savedPositions = JSON.parse(saved);
        // Merge saved positions with default icons (in case new icons were added)
        return defaultIcons.map(defaultIcon => {
          const savedIcon = savedPositions.find((icon: DesktopIconItem) => icon.id === defaultIcon.id);
          return savedIcon ? { ...defaultIcon, x: savedIcon.x, y: savedIcon.y } : defaultIcon;
        });
      }
    } catch (error) {
      console.warn('Failed to load icon positions from localStorage:', error);
    }
    return defaultIcons;
  };

  const [desktopIcons, setDesktopIcons] = useState<DesktopIconItem[]>(loadIconPositions);

  // Save icon positions to localStorage
  const saveIconPositions = (icons: DesktopIconItem[]) => {
    try {
      localStorage.setItem('desktop-icon-positions', JSON.stringify(icons));
    } catch (error) {
      console.warn('Failed to save icon positions to localStorage:', error);
    }
  };

  // Update icon position when dragged
  const updateIconPosition = (iconId: string, newX: number, newY: number) => {
    setDesktopIcons(prevIcons => {
      const updatedIcons = prevIcons.map(icon =>
        icon.id === iconId ? { ...icon, x: newX, y: newY } : icon
      );
      saveIconPositions(updatedIcons);
      return updatedIcons;
    });
  };

  // Handle desktop selection rectangle
  const handleDesktopMouseDown = (e: React.MouseEvent) => {
    // Only start selection if clicking on empty desktop space (not on icons or windows)
    if (e.target === e.currentTarget) {
      const rect = e.currentTarget.getBoundingClientRect();
      const startX = e.clientX - rect.left;
      const startY = e.clientY - rect.top;
      
      setSelectionRect({
        startX,
        startY,
        endX: startX,
        endY: startY,
        isSelecting: true,
      });
    }
  };

  // Handle selection rectangle dragging
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (selectionRect.isSelecting) {
        const desktopElement = document.querySelector('.desktop') as HTMLElement;
        if (desktopElement) {
          const rect = desktopElement.getBoundingClientRect();
          const endX = e.clientX - rect.left;
          const endY = e.clientY - rect.top;
          
          setSelectionRect(prev => ({
            ...prev,
            endX,
            endY,
          }));
        }
      }
    };

    const handleMouseUp = () => {
      setSelectionRect(prev => ({
        ...prev,
        isSelecting: false,
      }));
    };

    if (selectionRect.isSelecting) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [selectionRect.isSelecting]);

  // Load positions on component mount
  useEffect(() => {
    setDesktopIcons(loadIconPositions());
  }, []);

  // Handle admin panel access (Ctrl+Shift+A)
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setShowAdminPanel(true);
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, []);

  // Handle stream state changes from admin panel
  const handleStreamStateChange = (isLive: boolean, streamData?: LiveStreamData) => {
    setIsStreamLive(isLive);
    setLiveStreamData(streamData || null);
  };

  // Calculate selection rectangle dimensions and position
  const getSelectionRectStyle = () => {
    if (!selectionRect.isSelecting) return { display: 'none' };

    const left = Math.min(selectionRect.startX, selectionRect.endX);
    const top = Math.min(selectionRect.startY, selectionRect.endY);
    const width = Math.abs(selectionRect.endX - selectionRect.startX);
    const height = Math.abs(selectionRect.endY - selectionRect.startY);

    return {
      left: `${left}px`,
      top: `${top}px`,
      width: `${width}px`,
      height: `${height}px`,
      display: 'block',
    };
  };

  return (
    <div 
      className="desktop"
      onMouseDown={handleDesktopMouseDown}
    >
      {/* Desktop Icons */}
      {desktopIcons.map((icon) => (
        <DesktopIcon
          key={icon.id}
          id={icon.id}
          name={icon.name}
          icon={icon.icon}
          x={icon.x}
          y={icon.y}
          type={icon.type}
          onPositionChange={updateIconPosition}
          isStreamLive={isStreamLive}
          liveStreamData={liveStreamData}
        />
      ))}
      
      {/* Selection Rectangle */}
      <div 
        className="selection-rectangle"
        style={getSelectionRectStyle()}
      />
      
      {/* Windows */}
      {windows.map((window) => (
        <Window
          key={window.id}
          id={window.id}
          title={window.title}
          x={window.x}
          y={window.y}
          width={window.width}
          height={window.height}
          isMinimized={window.isMinimized}
          isMaximized={window.isMaximized}
          isActive={window.isActive}
          content={window.content}
        />
      ))}
      
      {/* Taskbar */}
      <Taskbar />

      {/* Admin Panel */}
      <AdminPanel
        isVisible={showAdminPanel}
        onClose={() => setShowAdminPanel(false)}
        onStreamStateChange={handleStreamStateChange}
      />
    </div>
  );
};

export default Desktop;